// نظام الكشف الذكي للأعمدة
export interface ColumnSuggestion {
  columnName: string;
  confidence: number;
  reasons: string[];
  category: 'workshop' | 'content' | 'trainer' | 'organization' | 'question' | 'identifier';
}

export interface DetectionResult {
  workshopColumns: ColumnSuggestion[];
  groupingColumns: ColumnSuggestion[];
  questionColumns: string[];
  identifierColumns: string[];
}

export class ColumnDetector {
  // كلمات مفتاحية للبحث عن عمود الورش/المجموعات
  private static readonly WORKSHOP_KEYWORDS = [
    // العربية
    'ورشة', 'ورش', 'دورة', 'دورات', 'مادة', 'مواد', 'مقرر', 'مقررات',
    'برنامج', 'برامج', 'نشاط', 'أنشطة', 'فعالية', 'فعاليات', 'حدث', 'أحداث',
    'موضوع', 'مواضيع', 'عنوان', 'عناوين', 'اسم', 'أسماء', 'تدريب', 'تدريبات',
    'جلسة', 'جلسات', 'محاضرة', 'محاضرات', 'لقاء', 'لقاءات', 'اجتماع', 'اجتماعات',
    'منهج', 'مناهج', 'كورس', 'سلسلة', 'سلاسل', 'قسم', 'أقسام', 'شعبة', 'شعب',
    'مجموعة', 'مجموعات', 'فئة', 'فئات', 'نوع', 'أنواع', 'صنف', 'أصناف',
    
    // English
    'workshop', 'workshops', 'course', 'courses', 'training', 'trainings',
    'program', 'programs', 'session', 'sessions', 'activity', 'activities',
    'event', 'events', 'subject', 'subjects', 'topic', 'topics',
    'lecture', 'lectures', 'seminar', 'seminars', 'class', 'classes',
    'module', 'modules', 'unit', 'units', 'section', 'sections',
    'category', 'categories', 'type', 'types', 'group', 'groups',
    'name', 'title', 'department', 'departments'
  ];

  // كلمات مفتاحية للمحتوى
  private static readonly CONTENT_KEYWORDS = [
    'محتوى', 'مادة', 'موضوع', 'مضمون', 'منهج', 'مناهج', 'معلومات',
    'content', 'material', 'curriculum', 'syllabus', 'information'
  ];

  // كلمات مفتاحية للمدرب
  private static readonly TRAINER_KEYWORDS = [
    'مدرب', 'مدربة', 'محاضر', 'محاضرة', 'مقدم', 'مقدمة', 'معلم', 'معلمة',
    'أستاذ', 'دكتور', 'مرشد', 'مرشدة', 'موجه', 'موجهة',
    'trainer', 'instructor', 'teacher', 'lecturer', 'facilitator',
    'presenter', 'professor', 'doctor', 'guide'
  ];

  // كلمات مفتاحية للتنظيم
  private static readonly ORGANIZATION_KEYWORDS = [
    'تنظيم', 'مكان', 'قاعة', 'تجهيز', 'ترتيب', 'إعداد', 'تحضير',
    'بيئة', 'مرافق', 'خدمات', 'استقبال', 'ضيافة',
    'organization', 'venue', 'room', 'facility', 'setup',
    'arrangement', 'environment', 'services', 'reception'
  ];

  // كلمات مفتاحية للمعرفات والترقيم
  private static readonly IDENTIFIER_KEYWORDS = [
    // العربية
    'رقم', 'ترقيم', 'تسلسل', 'معرف', 'كود', 'شفرة', 'مسلسل', 'فهرس',
    'ت', 'م', 'ر', 'تسلسلي', 'فريد', 'وحيد', 'منفرد',
    
    // English
    'id', 'identifier', 'index', 'number', 'num', 'no', 'code', 'serial',
    'sequence', 'seq', 'unique', 'key', 'pk', 'primary', 'auto', 'increment',
    'row', 'record', 'entry', 'item', 'count', 'counter', '#'
  ];

  /**
   * تحليل الأعمدة واكتشاف أنواعها
   */
  static detectColumns(headers: string[], sampleData?: Record<string, any>[]): DetectionResult {
    const workshopColumns: ColumnSuggestion[] = [];
    const groupingColumns: ColumnSuggestion[] = [];
    const questionColumns: string[] = [];
    const identifierColumns: string[] = [];

    console.log('🔍 بدء تحليل الأعمدة:', headers);

    headers.forEach(header => {
      const analysis = this.analyzeColumn(header, sampleData);
      
      console.log(`📊 العمود "${header}":`, {
        category: analysis.category,
        confidence: analysis.confidence.toFixed(2),
        reasons: analysis.reasons
      });
      
      if (analysis.category === 'identifier') {
        identifierColumns.push(header);
        console.log(`🆔 تم تصنيف "${header}" كعمود معرف - سيتم استبعاده من الأسئلة`);
      } else if (analysis.category === 'workshop') {
        workshopColumns.push(analysis);
      } else if (analysis.confidence > 0.3) {
        groupingColumns.push(analysis);
      } else {
        questionColumns.push(header);
      }
    });

    // ترتيب النتائج حسب الثقة
    workshopColumns.sort((a, b) => b.confidence - a.confidence);
    groupingColumns.sort((a, b) => b.confidence - a.confidence);

    console.log('✅ نتائج التحليل:', {
      identifierColumns: identifierColumns.length,
      workshopColumns: workshopColumns.length,
      groupingColumns: groupingColumns.length,
      questionColumns: questionColumns.length
    });

    return {
      workshopColumns,
      groupingColumns,
      questionColumns,
      identifierColumns
    };
  }

  /**
   * تحليل عمود واحد
   */
  private static analyzeColumn(header: string, sampleData?: Record<string, any>[]): ColumnSuggestion {
    const normalizedHeader = header.toLowerCase().trim();
    let confidence = 0;
    const reasons: string[] = [];
    let category: ColumnSuggestion['category'] = 'question';

    // فحص خاص لأعمدة ID (أولوية قصوى)
    const idScore = this.calculateKeywordScore(normalizedHeader, this.IDENTIFIER_KEYWORDS);
    if (idScore > 0.3 || normalizedHeader === 'id' || normalizedHeader === 'رقم' || normalizedHeader === 'ت') {
      confidence = 0.95; // ثقة عالية جداً
      reasons.push(`يبدو أنه عمود معرف/ترقيم (${idScore.toFixed(2)})`);
      category = 'identifier';
      
      // تحليل إضافي للبيانات إذا متوفرة
      if (sampleData && sampleData.length > 0) {
        const values = sampleData.slice(0, 10).map(row => row[header]).filter(v => v != null && v !== '');
        const numericValues = values.filter(v => typeof v === 'number' || !isNaN(Number(v)));
        
        // إذا كانت كلها أرقام متتالية أو شبه متتالية، فهو ID بالتأكيد
        if (numericValues.length === values.length && values.length > 3) {
          const sortedValues = numericValues.map(v => Number(v)).sort((a, b) => a - b);
          const isSequential = sortedValues.every((val, index) => 
            index === 0 || (val - sortedValues[index - 1]) <= 3
          );
          
          if (isSequential) {
            confidence = 0.99;
            reasons.push('القيم متتالية أو شبه متتالية - يؤكد أنه عمود ترقيم');
          }
        }
      }
      
      return { columnName: header, confidence, category, reasons };
    }

    // فحص الكلمات المفتاحية لأعمدة الورش
    const workshopScore = this.calculateKeywordScore(normalizedHeader, this.WORKSHOP_KEYWORDS);
    if (workshopScore > 0) {
      confidence += workshopScore * 0.8;
      reasons.push(`يحتوي على كلمات مفتاحية للورش/المجموعات (${workshopScore.toFixed(2)})`);
      category = 'workshop';
    }

    // فحص الكلمات المفتاحية للمحتوى
    const contentScore = this.calculateKeywordScore(normalizedHeader, this.CONTENT_KEYWORDS);
    if (contentScore > 0) {
      confidence += contentScore * 0.6;
      reasons.push(`يحتوي على كلمات مفتاحية للمحتوى (${contentScore.toFixed(2)})`);
      if (category === 'question') category = 'content';
    }

    // فحص الكلمات المفتاحية للمدرب
    const trainerScore = this.calculateKeywordScore(normalizedHeader, this.TRAINER_KEYWORDS);
    if (trainerScore > 0) {
      confidence += trainerScore * 0.6;
      reasons.push(`يحتوي على كلمات مفتاحية للمدرب (${trainerScore.toFixed(2)})`);
      if (category === 'question') category = 'trainer';
    }

    // فحص الكلمات المفتاحية للتنظيم
    const orgScore = this.calculateKeywordScore(normalizedHeader, this.ORGANIZATION_KEYWORDS);
    if (orgScore > 0) {
      confidence += orgScore * 0.6;
      reasons.push(`يحتوي على كلمات مفتاحية للتنظيم (${orgScore.toFixed(2)})`);
      if (category === 'question') category = 'organization';
    }

    // تحليل البيانات إذا متوفرة
    if (sampleData && sampleData.length > 0) {
      const dataAnalysis = this.analyzeColumnData(header, sampleData);
      confidence += dataAnalysis.confidenceBoost;
      reasons.push(...dataAnalysis.reasons);
      
      if (dataAnalysis.category && (category === 'question' || dataAnalysis.category === 'identifier')) {
        category = dataAnalysis.category;
      }
    }

    // تحسين للأعمدة القصيرة (قد تكون اختصارات)
    if (normalizedHeader.length <= 2) {
      confidence += 0.1;
      reasons.push('عمود بحرف واحد أو حرفين - قد يكون معرف');
    }

    return { columnName: header, confidence, category, reasons };
  }

  /**
   * حساب درجة التطابق مع الكلمات المفتاحية
   */
  private static calculateKeywordScore(text: string, keywords: string[]): number {
    let score = 0;
    let maxWordScore = 0;

    keywords.forEach(keyword => {
      const keywordLower = keyword.toLowerCase();
      
      // تطابق كامل
      if (text === keywordLower) {
        score += 1;
      }
      // يحتوي على الكلمة كاملة
      else if (text.includes(keywordLower)) {
        const wordScore = keywordLower.length / text.length;
        maxWordScore = Math.max(maxWordScore, wordScore);
      }
      // تطابق جزئي (للكلمات الطويلة)
      else if (keywordLower.length > 3) {
        const similarity = this.calculateSimilarity(text, keywordLower);
        if (similarity > 0.7) {
          maxWordScore = Math.max(maxWordScore, similarity * 0.8);
        }
      }
    });

    return Math.max(score, maxWordScore);
  }

  /**
   * حساب التشابه بين نصين
   */
  private static calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * حساب مسافة ليفنشتاين
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * تحليل البيانات الفعلية في العمود
   */
  private static analyzeColumnData(columnName: string, sampleData: Record<string, any>[]): {
    confidenceBoost: number;
    reasons: string[];
    category?: ColumnSuggestion['category'];
  } {
    const values = sampleData.slice(0, 10).map(row => row[columnName]).filter(v => v != null && v !== '');
    
    if (values.length === 0) {
      return { confidenceBoost: 0, reasons: [] };
    }

    const reasons: string[] = [];
    let confidenceBoost = 0;
    let category: ColumnSuggestion['category'] | undefined;

    // تحليل أنواع البيانات
    const uniqueValues = new Set(values.map(v => String(v).trim()));
    const uniqueCount = uniqueValues.size;
    const totalCount = values.length;
    
    console.log(`🔍 تحليل العمود "${columnName}":`, {
      totalValues: totalCount,
      uniqueValues: uniqueCount,
      ratio: uniqueCount / totalCount,
      sampleValues: Array.from(uniqueValues).slice(0, 5)
    });
    
    // إذا كانت القيم فريدة تماماً، قد يكون معرف
    if (uniqueCount === totalCount && totalCount > 3) {
      confidenceBoost += 0.2; // خفض من 0.3
      reasons.push('قيم فريدة - قد يكون معرف');
      category = 'identifier';
    }
    
    // تحليل القيم للتأكد من أنها ليست إجابات استبيان
    const commonResponseValues = [
      'موافق بشدة', 'موافق', 'محايد', 'غير موافق', 'غير موافق بشدة',
      'strongly agree', 'agree', 'neutral', 'disagree', 'strongly disagree',
      'ممتاز', 'جيد جدا', 'جيد', 'مقبول', 'ضعيف',
      'excellent', 'very good', 'good', 'fair', 'poor',
      'نعم', 'لا', 'yes', 'no', '1', '2', '3', '4', '5'
    ];
    
    // فحص إذا كانت القيم تشبه إجابات الاستبيان
    const valuesArray = Array.from(uniqueValues).map(v => String(v).toLowerCase().trim());
    const responseMatches = valuesArray.filter(val => 
      commonResponseValues.some(response => 
        response.toLowerCase() === val || val.includes(response.toLowerCase())
      )
    ).length;
    
    const isLikelyResponseColumn = responseMatches >= uniqueCount * 0.5; // إذا 50% أو أكثر من القيم تشبه إجابات
    
    if (isLikelyResponseColumn) {
      console.log(`🚫 العمود "${columnName}" يبدو كعمود إجابات استبيان - سيتم استبعاده من الورش`);
      console.log(`   القيم المشبوهة: [${valuesArray.slice(0, 5).join(', ')}]`);
      return { confidenceBoost: 0, reasons: ['يبدو كعمود إجابات استبيان'], category: 'question' };
    }

    // إذا كان عدد القيم الفريدة قليل مقارنة بالإجمالي، قد يكون مجموعات
    const ratio = uniqueCount / totalCount;
    if (ratio <= 0.5 && uniqueCount > 1 && uniqueCount <= 10) {
      confidenceBoost += 0.6;
      reasons.push(`تنوع محدود في القيم (${uniqueCount}/${totalCount}) - مناسب للتجميع`);
      if (!category) category = 'workshop';
    }

    // فحص إذا كانت القيم تحتوي على كلمات مفتاحية
    const valueText = Array.from(uniqueValues).join(' ').toLowerCase();
    const workshopScore = this.calculateKeywordScore(valueText, this.WORKSHOP_KEYWORDS);
    if (workshopScore > 0.1) { // خفض من 0.2
      confidenceBoost += workshopScore * 0.4; // زيادة التأثير
      reasons.push(`البيانات تحتوي على كلمات مفتاحية ذات صلة (${workshopScore.toFixed(2)})`);
      if (!category) category = 'workshop';
    }

    return { confidenceBoost, reasons, category };
  }

  /**
   * اقتراح أفضل عمود للورش
   */
  static suggestBestWorkshopColumn(detectionResult: DetectionResult): ColumnSuggestion | null {
    console.log('🤖 تحليل اقتراحات الأعمدة:', {
      workshopColumns: detectionResult.workshopColumns.length,
      groupingColumns: detectionResult.groupingColumns.length,
      questionColumns: detectionResult.questionColumns.length
    });

    // فلترة الأعمدة التي لا تبدو كإجابات استبيان
    const filteredWorkshopColumns = detectionResult.workshopColumns.filter(col => {
      return !col.reasons.some(reason => reason.includes('يبدو كعمود إجابات استبيان'));
    });
    
    const filteredGroupingColumns = detectionResult.groupingColumns.filter(col => {
      return !col.reasons.some(reason => reason.includes('يبدو كعمود إجابات استبيان'));
    });

    console.log('🔍 الأعمدة بعد الفلترة:', {
      workshopColumns: filteredWorkshopColumns.length,
      groupingColumns: filteredGroupingColumns.length
    });

    // إذا وجدت أعمدة ورش مفلترة، اختر الأفضل
    if (filteredWorkshopColumns.length > 0) {
      const best = filteredWorkshopColumns[0];
      console.log('✅ اختيار أفضل عمود ورش مفلتر:', best.columnName, 'بثقة:', best.confidence);
      return best;
    }
    
    // إذا لم توجد أعمدة ورش مفلترة، ابحث في أعمدة التجميع المفلترة
    if (filteredGroupingColumns.length > 0) {
      const best = filteredGroupingColumns[0];
      console.log('⚠️ اختيار أفضل عمود تجميع مفلتر:', best.columnName, 'بثقة:', best.confidence);
      return best;
    }
    
    // إذا لم نجد شيء مفلتر، ارجع للأعمدة الأصلية كملاذ أخير
    if (detectionResult.workshopColumns.length > 0) {
      const best = detectionResult.workshopColumns[0];
      console.log('⚠️ استخدام عمود ورش غير مفلتر كملاذ أخير:', best.columnName);
      return best;
    }
    
    if (detectionResult.groupingColumns.length > 0) {
      const best = detectionResult.groupingColumns[0];
      console.log('⚠️ استخدام عمود تجميع غير مفلتر كملاذ أخير:', best.columnName);
      return best;
    }
    
    console.log('❌ لم يتم العثور على اقتراحات مناسبة');
    return null;
  }

  /**
   * تجميع الأسئلة في محاور ذكية (مع استبعاد أعمدة ID)
   */
  static groupQuestionsIntoAxes(questionHeaders: string[], sampleData?: Record<string, any>[]): Record<string, string[]> {
    // أولاً: فلترة أعمدة ID والمعرفات
    const filteredQuestions = questionHeaders.filter(header => {
      const normalizedHeader = header.toLowerCase().trim();
      
      // فحص الكلمات المفتاحية لـ ID
      const idScore = this.calculateKeywordScore(normalizedHeader, this.IDENTIFIER_KEYWORDS);
      const isLikelyId = idScore > 0.2 || 
                        normalizedHeader === 'id' || 
                        normalizedHeader === 'رقم' || 
                        normalizedHeader === 'ت' ||
                        normalizedHeader.length <= 2;
      
      if (isLikelyId) {
        console.log(`🚫 استبعاد العمود "${header}" من الأسئلة (يبدو كمعرف)`);
        return false;
      }
      
      // فحص إضافي للبيانات إذا متوفرة
      if (sampleData && sampleData.length > 0) {
        const values = sampleData.slice(0, 5).map(row => row[header]).filter(v => v != null && v !== '');
        const numericValues = values.filter(v => typeof v === 'number' || !isNaN(Number(v)));
        
        // إذا كانت كلها أرقام كبيرة (مثل ID)، استبعدها
        if (numericValues.length === values.length && values.length > 2) {
          const avgValue = numericValues.reduce((sum, v) => sum + Number(v), 0) / numericValues.length;
          if (avgValue > 10) { // متوسط كبير جداً - يؤشر لـ ID
            console.log(`🚫 استبعاد العمود "${header}" من الأسئلة (متوسط عالي: ${avgValue.toFixed(1)})`);
            return false;
          }
        }
      }
      
      return true;
    });

    console.log(`📋 الأسئلة المفلترة: ${filteredQuestions.length} من ${questionHeaders.length}`);

    const axes: Record<string, string[]> = {};
    const usedQuestions = new Set<string>();

    // إذا لم تبق أسئلة بعد الفلترة، إنشاء محور عام
    if (filteredQuestions.length === 0) {
      axes['محور عام'] = [];
      return axes;
    }

    // تجميع الأسئلة حسب الكلمات المفتاحية
    filteredQuestions.forEach(question => {
      if (usedQuestions.has(question)) return;

      const normalizedQuestion = question.toLowerCase();
      
      if (this.calculateKeywordScore(normalizedQuestion, this.CONTENT_KEYWORDS) > 0.3) {
        if (!axes['محور المحتوى والمادة العلمية']) axes['محور المحتوى والمادة العلمية'] = [];
        axes['محور المحتوى والمادة العلمية'].push(question);
        usedQuestions.add(question);
      } else if (this.calculateKeywordScore(normalizedQuestion, this.TRAINER_KEYWORDS) > 0.3) {
        if (!axes['محور المدرب والتقديم']) axes['محور المدرب والتقديم'] = [];
        axes['محور المدرب والتقديم'].push(question);
        usedQuestions.add(question);
      } else if (this.calculateKeywordScore(normalizedQuestion, this.ORGANIZATION_KEYWORDS) > 0.3) {
        if (!axes['محور التنظيم والإدارة']) axes['محور التنظيم والإدارة'] = [];
        axes['محور التنظيم والإدارة'].push(question);
        usedQuestions.add(question);
      }
    });

    // إضافة الأسئلة المتبقية لمحور عام
    const remainingQuestions = filteredQuestions.filter(q => !usedQuestions.has(q));
    if (remainingQuestions.length > 0) {
      axes['محور عام'] = remainingQuestions;
    }

    // تنظيف المحاور الفارغة
    Object.keys(axes).forEach(axisName => {
      if (axes[axisName].length === 0) {
        delete axes[axisName];
      }
    });

    console.log('📊 تجميع الأسئلة في محاور:', Object.entries(axes).map(([name, questions]) => `${name}: ${questions.length}`));

    return axes;
  }
} 